import { Injectable, Injector } from "@angular/core";
import { HttpWrapperService } from "./http.wrapper.service";
import { SessionManagerService } from "./SessionManager/session-manager.service";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { DiscountMethod } from "../admin/discounts/discount-models/DiscountMethod";
import { SaveActionResult } from "../admin/events-screen/event-page-models/save-action-result";
import { EditDiscountFormState } from "../admin/discounts/discount-models/EditDiscountFormState";
import { observableToPromise } from "../admin/events-screen/utils/observableToPromise";
import { BaseDiscountModel } from "../admin/discounts/discount-models/BaseDiscountModel";
import { Observable } from "rxjs/Observable";
import { ServerNamespace } from "../_models/server/server-namespace";
import { DateAndTimeUtils } from "../_helpers/dateUtils";
import { NumbersUtils } from "../_helpers/NumbersUtils";
import { GenericSearchState } from "../_models/GenericSearchState";
import { ChildAbstractModel } from "../_models/child-abstract-model";
import { DevToolsService } from "./dev-tools.service";
import { PatchDiscountModel } from "../admin/discounts/discount-models/PatchDiscountModel";
import { ParentEventFromSearchWithSelectedChildEvents } from "../admin/events-screen/event-page-models/parent-event-from-search";
import {
  ChildDiscountModel,
  ChildDiscountStatus,
} from "../admin/discounts/discount-models/ChildDiscountModel";
import { ChildEvent } from "../admin/events-screen/event-page-models/child-event";
import { FilesService } from "./files.service";
import { ResponseContentType } from "@angular/http";

@Injectable()
export class DiscountManagementService {
  private readonly discountsInTable$: BehaviorSubject<BaseDiscountModel[]> =
    new BehaviorSubject([]);
  private readonly discountTableSearchState$: BehaviorSubject<GenericSearchState> =
    new BehaviorSubject<GenericSearchState>(null);
  private readonly discountSearchResultsAbstract$: BehaviorSubject<{
    numberOfDiscount: number;
    totalSum: string;
    lastSearchParams: Record<string, any>;
  }> = new BehaviorSubject<{
    numberOfDiscount: number;
    totalSum: string;
    lastSearchParams: {};
  } | null>(null);
  private MAX_NUMBER_OF_PARENT_EVENTS_ON_SCROLL: 200;

  constructor(
    private http: HttpWrapperService,
    private sessionManager: SessionManagerService,
    private injector: Injector,
    private router: Router,
    private devToolsService: DevToolsService,
    private filesService: FilesService
  ) {}

  public static serializeServerParentEventForNewDiscount(
    parentEventFromServer: ServerNamespace.GetParentEventForNewSubsidy
  ): ParentEventFromSearchWithSelectedChildEvents {
    const unImportantBaseEventData = {
      brotherDiscount: false,
      closedToMembers: false,
      eventEndDate: "",
      eventEndHour: "",
      eventIndex: "",
      eventStartDate: "",
      eventStartHour: "",
      eventStatus: undefined,
      eventType: 0,
      gainCenter1: "",
      gainCenter2: "",
      prices: "",
      registeredChildren: 0,
      registrationStartDate: undefined,
      registrationEndDate: undefined,
    };

    return {
      ...unImportantBaseEventData,
      childEvents: parentEventFromServer.events.map((childEvent) => {
        return {
          ...unImportantBaseEventData,
          eventName: childEvent.name,
          eventOpenTo: childEvent.open_to_details,
          parentEventId: parentEventFromServer.id,
          parentEventCode: parentEventFromServer.code,
          childEventCode: childEvent.code,
          childEventId: childEvent.id,
          childEventPrice: childEvent.price,
          circle: [],
          nests: [],
          layers: [],
          childEventPriceChange: [],
          eventCollection: [],
          isSelected: true,
        };
      }),

      eventName: parentEventFromServer.name,
      eventOpenTo: parentEventFromServer.open_to_details,
      parentEventCode: parentEventFromServer.code,
      parentEventId: parentEventFromServer.id,
      prices: "",
      registeredChildren: 0,
      registrationEndDate: DateAndTimeUtils.serverEpochToDate(
        parentEventFromServer.registration_end_date
      ),
      registrationStartDate: undefined,
    };
  }

  public static serializeServerChildForNewDiscount(
    childrenForNewSubsidy: ServerNamespace.GetChanichForNewSubsidy
  ): ChildDiscountModel {
    const serializedStatus = (childStatus: string) => {
      switch (childStatus) {
        case "no-events-have-a-discount":
          return ChildDiscountStatus.canAddDiscount;
        case "all-events-have-a-discount":
          return ChildDiscountStatus.AlreadyHasDiscount;
        case "some-events-have-a-discount":
          return ChildDiscountStatus.HasSomeDiscounts;
        default:
          return ChildDiscountStatus.canAddDiscount;
      }
    };

    return {
      id: childrenForNewSubsidy.id,
      firstName: childrenForNewSubsidy.first_name,
      lastName: childrenForNewSubsidy.last_name,
      israeliId: childrenForNewSubsidy.personal_id,
      address: "",
      nest: childrenForNewSubsidy.ken,
      layer: childrenForNewSubsidy.grade,
      parents: "",
      discountEvents: childrenForNewSubsidy.existing_subsidies.map((event) => {
        return {
          childEventId: event.event_id.toString(),
          hasDiscount: event.subsidy_exists,
          currentPrice: NaN,
          existingDiscount: NumbersUtils.decimalToNumber(
            event.final_subsidy_amount
          ),
        };
      }),
      childStatus: serializedStatus(childrenForNewSubsidy.subsidy_status),
    };
  }

  public static serializeServerChildRequestRequestsToFamilyMember(
    childFromServer: ServerNamespace.GetChanichForSubsidySearch
  ): ChildAbstractModel {
    return {
      childId: childFromServer.id,
      firstName: childFromServer.first_name,
      lastName: childFromServer.last_name,
      israeliId: childFromServer.personal_id,
      nest: childFromServer.ken,
      layer: childFromServer.grade,
    };
  }

  public static nestFromFromOrgans({
    kenim,
    mehozot,
    kehilot,
  }: {
    kenim?: number[];
    mehozot?: number[];
    kehilot?: number[];
  }) {
    const nestForm = [];
    if (kenim) {
      for (const ken of kenim) {
        nestForm.push({ id: ken, type: "nest" });
      }
    }
    if (mehozot) {
      for (const mehoz of mehozot) {
        nestForm.push({ id: mehoz, type: "county" });
      }
    }
    if (kehilot) {
      for (const kehilotId of kehilot) {
        nestForm.push({ id: kehilotId, type: "circle" });
      }
    }
    return nestForm;
  }

  public serverDiscountToBaseDiscount(
    serverSubsidy: ServerNamespace.ServerBaseSubsidy
  ): BaseDiscountModel {
    const israeliId = this.devToolsService.removeStartsFromIsraeliId(
      serverSubsidy.hanich.personal_id
    );

    return {
      discountAmount: NumbersUtils.decimalToNumber(
        serverSubsidy.final_subsidy_amount
      ),
      approvedBy: serverSubsidy.created_by,
      canEdit: serverSubsidy.user_can_edit,
      childCounty: serverSubsidy.hanich.mahoz,
      childEventCode: serverSubsidy.event.code,
      childEventName: serverSubsidy.event.name,
      childFirstName: serverSubsidy.hanich.first_name,
      childId: serverSubsidy.hanich.id,
      childIsraeliId: israeliId,
      childLastName: serverSubsidy.hanich.last_name,
      childLayer: serverSubsidy.hanich.grade,
      childNest: serverSubsidy.hanich.ken,
      childPayed: serverSubsidy.hanich_is_registered_to_event,
      comments: serverSubsidy.comments,
      discountDate: DateAndTimeUtils.serverEpochToDate(
        serverSubsidy.updated_at
      ),
      discountId: serverSubsidy.id,
      priceAfterDiscount: NumbersUtils.decimalToNumber(
        serverSubsidy.final_price_to_pay
      ),
      originalPrice: serverSubsidy.original_event_price,
      parentEventCode: serverSubsidy.parent_event.code,
      parentEventName: serverSubsidy.parent_event.name,
      passedCommittee: serverSubsidy.passed_subsidies_committee,
      numberOfPayments: serverSubsidy.number_of_payments,
    };
  }

  public async scrolledToBottomOfResults() {
    const searchState = this.discountTableSearchState$.getValue();
    if (!searchState) {
      return;
    }
    const allUsersInTable = this.discountsInTable$.getValue();
    if (
      searchState.isLoading ||
      !searchState.next ||
      allUsersInTable.length > this.MAX_NUMBER_OF_PARENT_EVENTS_ON_SCROLL
    ) {
      return;
    }
    try {
      this.discountTableSearchState$.next({ ...searchState, isLoading: true });
      // scrolling
      const addressParams = searchState.next.split(`subsidy/?`)[1];
      const response = await observableToPromise(
        this.http.get(`django-api/subsidy/?${addressParams}`)
      );

      const resultsData =
        (await response.json()) as ServerNamespace.GetSubsidySearch;
      const serializedDiscounts = (resultsData.results || []).map((user) =>
        this.serverDiscountToBaseDiscount(user)
      );

      const existingDiscounts = allUsersInTable.map((parentEvent) => ({
        ...parentEvent,
      }));
      const allExistingParentEventsIds = existingDiscounts.map(
        (pe) => pe.discountId
      );
      const newParentEventsWithoutDuplicates = serializedDiscounts.filter(
        (pe) => !allExistingParentEventsIds.includes(pe.discountId)
      );

      existingDiscounts.push(...newParentEventsWithoutDuplicates);

      this.discountsInTable$.next(existingDiscounts);
      this.discountTableSearchState$.next({
        next: resultsData.next,
        count: resultsData.count,
        previous: resultsData.previous,
        isLoading: false,
      });
    } catch (err) {}
  }

  public calculatePriceAfterDiscountFromData(
    price: number,
    discountAmount: { type: DiscountMethod; value: number }
  ) {
    return discountAmount.type === DiscountMethod.Number
      ? this.calculateDiscountedPriceBasedOnNumber(price, discountAmount.value)
      : this.calculateDiscountedPriceBasedOnPercent(
          price,
          discountAmount.value
        );
  }

  public async createNewDiscount(
    newSubsidies: ServerNamespace.PostNewSubsidy[]
  ): Promise<SaveActionResult> {
    try {
      const response = await observableToPromise(
        this.http.post(`/django-api/subsidy/`, newSubsidies)
      );
      if (response.status === 201) {
        return {
          success: true,
          data: response.json(),
        };
      }
      return {
        success: false,
        error: response.json(),
      };
    } catch (e) {
      return {
        success: false,
        error: e,
      };
    }
  }

  public async submitNewDiscountForm(
    discountId: string,
    patchData: PatchDiscountModel
  ): Promise<SaveActionResult> {
    try {
      const response = await observableToPromise(
        this.http.patch(`/django-api/subsidy/${discountId}/`, patchData)
      );
      if (response.status === 200) {
        return {
          success: true,
          data: response.json(),
        };
      }
      return {
        success: false,
        error: response.json(),
      };
    } catch (e) {
      return {
        success: false,
        error: e,
      };
    }
  }

  public getAllDiscountsInTable(): Observable<BaseDiscountModel[]> {
    return this.discountsInTable$.asObservable();
  }

  public getDiscountSearchResultsAbstract(): Observable<{
    numberOfDiscount: number;
    totalSum: string;
  }> {
    return this.discountSearchResultsAbstract$.asObservable();
  }

  public async initSingleDiscountForm(
    discountFromSearch: BaseDiscountModel
  ): Promise<EditDiscountFormState> {
    const devFakeEditDiscountFormState: EditDiscountFormState = {
      amount: {
        type: DiscountMethod.Number,
        value: discountFromSearch.discountAmount,
      },
      childDiscountDescription: {
        address: "",
        childEventCode: discountFromSearch.childEventCode,
        firstName: discountFromSearch.childFirstName,
        id: discountFromSearch.discountId,
        israeliId: discountFromSearch.childIsraeliId,
        originalPrice: discountFromSearch.originalPrice,
        lastName: discountFromSearch.childLastName,
        layer: discountFromSearch.childLayer,
        nest: discountFromSearch.childNest,
        parentEventCode: discountFromSearch.parentEventCode,
        parentEventId: "",
        parentEventName: discountFromSearch.parentEventName,
        county: discountFromSearch.childCounty,
      },
      discountDetails: {
        comments: discountFromSearch.comments,
        numberOfPayments: discountFromSearch.numberOfPayments,
        passedCommittee: discountFromSearch.passedCommittee,
      },
    };
    return devFakeEditDiscountFormState;
  }

  public calculatePriceAfterDiscountFromFormPage(
    price: number,
    discountMethod: DiscountMethod,
    shekelDiscount: number,
    percentDiscount: number
  ) {
    return discountMethod === DiscountMethod.Number
      ? this.calculateDiscountedPriceBasedOnNumber(price, shekelDiscount)
      : this.calculateDiscountedPriceBasedOnPercent(price, percentDiscount);
  }

  public async exportToExcel() {
    const discountSearchResultsAbstract =
      this.discountSearchResultsAbstract$.getValue();
    const lastSearchParams = discountSearchResultsAbstract
      ? discountSearchResultsAbstract.lastSearchParams
      : {};
    const fileName = this.buildSubsidyExcelFileName(lastSearchParams);
    const response = await observableToPromise(
      this.http.get(`/django-api/subsidy/export_excel/`, {
        params: lastSearchParams,
        responseType: ResponseContentType.Blob,
      })
    );
    await this.filesService.downloadGeneralFile({ response, fileName });
  }

  public calculateInShekelsDiscountedBasedOnPercent(price, percent) {
    return Math.floor((price * percent) / 100);
  }

  public async searchDiscounts({
    limitToCurrentYear,
    hanich_id,
    parent_event_id,
    kenim,
    mehozot,
    kehilot,
    grades_ids,
  }: {
    limitToCurrentYear?: boolean;
    from_date?: string | null;
    to_date?: string | null;
    hanich_id?: string | null;
    parent_event_id?: string | null;
    kenim?: number[] | null;
    mehozot?: number[] | null;
    kehilot?: number[] | null;
    grades_ids?: number[] | null;
  }): Promise<BaseDiscountModel[]> {
    const nestForm = DiscountManagementService.nestFromFromOrgans({
      kenim,
      mehozot,
      kehilot,
    });
    const userSearchParams: any = {
      hanich_id,
      grades_ids,
      nestForm: nestForm.length ? JSON.stringify(nestForm) : null,
    };
    if (parent_event_id) {
      //@ts-ignore
      userSearchParams["parent_events_ids"] = [parent_event_id];
    }
    let activeParams = {
      limitToCurrentYear: !!limitToCurrentYear,
    };
    for (const key in userSearchParams) {
      if (userSearchParams[key]) {
        activeParams[key] = userSearchParams[key];
      }
    }
    activeParams = HttpWrapperService.ParamsObjectToCommaArrays(
      activeParams
    ) as any;

    const response = await observableToPromise(
      this.http.get(`/django-api/subsidy/`, { params: activeParams })
    );
    const resultsData =
      (await response.json()) as ServerNamespace.GetSubsidySearch;
    const users = (resultsData.results || []).map((user) =>
      this.serverDiscountToBaseDiscount(user)
    );

    this.discountsInTable$.next(users);
    this.discountTableSearchState$.next({
      next: resultsData.next,
      count: resultsData.count,
      previous: resultsData.previous,
      isLoading: false,
    });
    this.discountSearchResultsAbstract$.next({
      numberOfDiscount: resultsData.count,
      totalSum: resultsData.total_subsidy_sum,
      lastSearchParams: activeParams,
    });
    return users;
  }

  public async searchParentEventForNewSubsidyByText(
    searchText: string,
    maxResults: number = 10
  ): Promise<ParentEventFromSearchWithSelectedChildEvents[]> {
    if (!searchText || searchText.length < 2) {
      return [];
    }

    const mock = this.devToolsService.mockParentEventsForSearch;
    const response = await observableToPromise(
      this.http.get(
        `/django-api/subsidy/select_parent_event_for_subsidy_create/`,
        {
          params: { search: searchText },
        }
      )
    );

    const body = await response.json();
    const events =
      body.results as ServerNamespace.GetParentEventForNewSubsidy[];
    let data = events.map(
      DiscountManagementService.serializeServerParentEventForNewDiscount
    );
    data = data.map((event) => ({
      ...event,
    }));

    return data.slice(0, maxResults);
  }

  public async searchPChildForNewSubsidyByText({
    searchText,
    childEvents,
  }: {
    searchText: string;
    childEvents: ChildEvent[];
  }): Promise<ChildDiscountModel[]> {
    const childEventsIds = childEvents.map((c) => c.childEventId);
    const maxResults = 10;
    if (!searchText || searchText.length < 2) {
      return [];
    }

    const response = await observableToPromise(
      this.http.get(`/django-api/subsidy/select_hanichim_for_subsidy_create/`, {
        params: { search: searchText, event_ids: childEventsIds },
      })
    );

    const body = await response.json();
    const children = body.results as ServerNamespace.GetChanichForNewSubsidy[];
    const childrenWithoutPrices = children.map(
      DiscountManagementService.serializeServerChildForNewDiscount
    );
    const childrenWithPrices = childrenWithoutPrices.map((child) => {
      child.discountEvents = child.discountEvents.map((hanichPricingEvent) => {
        const onePricing = childEvents.find(
          (pricing) =>
            pricing.childEventId.toString() ===
            hanichPricingEvent.childEventId.toString()
        );
        if (!onePricing) {
          throw new Error(
            `Price for ${hanichPricingEvent.childEventId} event id not found`
          );
        }
        hanichPricingEvent.currentPrice = onePricing.childEventPrice;
        return {
          ...hanichPricingEvent,
          currentPrice: onePricing.childEventPrice,
        };
      });

      return {
        ...child,
      };
    });

    return childrenWithPrices.slice(0, maxResults);
  }

  public async searchEventByText(
    searchText: string,
    maxResults: number = 10
  ): Promise<ServerNamespace.GetParentEventForSubsidySearch[]> {
    if (!searchText || searchText.length < 3) {
      return [];
    }
    const response = await observableToPromise(
      this.http.get(
        `/django-api/subsidy/select_parent_events_for_subsidy_search/`,
        {
          params: { search: searchText },
        }
      )
    );
    const body = await response.json();
    const parentEvents =
      body.results as ServerNamespace.GetParentEventForSubsidySearch[];

    return parentEvents.slice(0, maxResults);
  }

  public async searchChildByText(
    searchText: string,
    maxResults: number = 10
  ): Promise<ChildAbstractModel[]> {
    if (!searchText || searchText.length < 3) {
      return [];
    }
    const response = await observableToPromise(
      this.http.get(`/django-api/subsidy/select_hanich_for_subsidy_search/`, {
        params: { search: searchText },
      })
    );
    const body = await response.json();
    const children =
      body.results as ServerNamespace.GetChanichForSubsidySearch[];
    let data = children.map(
      DiscountManagementService.serializeServerChildRequestRequestsToFamilyMember
    );
    data = data.map((child) => ({
      ...child,
      israeliId: this.devToolsService.removeStartsFromIsraeliId(
        child.israeliId
      ),
    }));

    return data.slice(0, maxResults);
  }

  public calculateFinalDiscountedPrice(
    price: number,
    discountAmount: {
      type: DiscountMethod;
      value: number;
    }
  ) {
    return discountAmount.type === DiscountMethod.Number
      ? discountAmount.value
      : Math.floor(price * (discountAmount.value / 100));
  }

  public async deleteDiscount(discountId: number): Promise<SaveActionResult> {
    try {
      const response = await observableToPromise(
        this.http.delete(`/django-api/subsidy/${discountId}/`)
      );
      if (response.status === 204) {
        return {
          success: true,
          data: response.json(),
        };
      }
      return {
        success: false,
        error: response.json(),
      };
    } catch (e) {
      return {
        success: false,
        error: e,
      };
    }
  }

  public calculateDiscountAmountBasedOnPercent(price: number, percent: number) {
    return Math.floor((price * percent) / 100);
  }

  private buildSubsidyExcelFileName(lastSearchParams: any) {
    const today = new Date();
    const pad = (n: number) => (n < 10 ? `0${n}` : n);
    const date = `${pad(today.getDate())}-${pad(today.getMonth() + 1)}`;

    const time = `${pad(today.getHours())}.${pad(today.getMinutes())}`;

    return `sibsudim_${date}_${time}.xlsx`;
  }

  private calculateDiscountedPriceBasedOnPercent(
    price: number,
    percent: number
  ) {
    return percent
      ? Math.floor(
          price - this.calculateDiscountAmountBasedOnPercent(price, percent)
        )
      : price;
  }

  private calculateDiscountedPriceBasedOnNumber(
    price: number,
    discount: number
  ) {
    return discount ? price - discount : price;
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/discount-management.service.ts